import React, { useEffect } from "react";
import { Table, Tag } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import { useSelector } from "react-redux";
import UseApi from "../../Reusable/CallApi/UseApi";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .title {
    font-size: 20px;
    font-weight: 500;
    color: #1f243b;
  }
`;

const STATUS_COLOR = {
  pending: "gold",
  passed: "green",
  failed: "red",
};

const formatDate = (value) => {
  const parsed = value ? dayjs(value) : null;
  return parsed && parsed.isValid() ? parsed.format("Do MMM YYYY") : "-";
};

// Read-only view of a person's own real scores - same
// tymeplushrlmscourseassessment data the Tutor's Scoring page shows,
// filtered server-side to just the viewed person (see the ?people_id=
// addition to getScores in handlers/tymeplushrlmsscoring/app.mjs).
// There's no separate learner login anywhere in this app - see
// learnerMenuItem's comment in Redux/systemSlice.js - viewingAsPeopleId
// (the header's "Viewing as" picker) is whose scores these are.
const MyScores = () => {
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const viewingAsPeopleId = useSelector((state) => state.systemTracking.viewingAsPeopleId);
  const { data, loading, request } = UseApi();

  useEffect(() => {
    if (!clientId || !viewingAsPeopleId) return;
    request({
      endPoint: `/scoring/getscores/${clientId}?people_id=${viewingAsPeopleId}`,
      method: "get",
    }).catch(() => {});
  }, [clientId, viewingAsPeopleId]);

  const scores = data?.response || [];

  const columns = [
    { title: "Assessment", dataIndex: "unit_title", key: "unit_title" },
    { title: "Course", dataIndex: "course_name", key: "course_name" },
    {
      title: "Scored on",
      dataIndex: "scored_on",
      key: "scored_on",
      render: formatDate,
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      render: (value) => value ?? "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Tag color={STATUS_COLOR[value] || "default"}>
          {value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}
        </Tag>
      ),
    },
  ];

  if (!viewingAsPeopleId) {
    return (
      <Wrapper>
        <div className="title">My Scores</div>
        <div>
          Pick who you're viewing as from the "Learner" dropdown in the header
          to see their real scores.
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="title">My Scores</div>
      <Table
        rowKey="assessment_id"
        columns={columns}
        dataSource={scores}
        loading={loading}
        pagination={false}
        locale={{ emptyText: "No scores recorded yet" }}
      />
    </Wrapper>
  );
};

export default MyScores;
